import React, { useState } from 'react';
import { CompleteKundali, Language, ChartStyle } from '../types/kundali';
import { AdvancedLifeAnalysisResult } from '../types/advancedLifeAnalysis';
import { PresentationRole } from '../types/role';
import { getNatalPromiseTier, getTimingConfluence } from '../utils/presentationHelper';
import { NorthIndianSvgChart, SouthIndianSvgChart } from './ChartViewer';
import { SwastikLogo } from './SwastikLogo';
import { downloadElementAsPdf } from '../utils/pdfExportUtil';
import { HtmlPrintPreviewModal } from './HtmlPrintPreviewModal';
import {
  Printer,
  ArrowLeft,
  Calendar,
  Compass,
  Award,
  Zap,
  BookOpen,
  Shield,
  Clock,
  Sparkles,
  CheckCircle2,
  FileText,
  Download,
  ExternalLink,
  Loader2
} from 'lucide-react';

interface AdvancedPrintReportProps {
  kundali: CompleteKundali;
  analysisResult: AdvancedLifeAnalysisResult;
  lang: Language;
  onClose: () => void;
  chartStyle?: ChartStyle;
  initialRole?: PresentationRole;
}

export const AdvancedPrintReport: React.FC<AdvancedPrintReportProps> = ({
  kundali,
  analysisResult,
  lang,
  onClose,
  chartStyle = 'north',
  initialRole = 'PRO'
}) => {
  const isHi = lang === 'hi';
  const [reportRole, setReportRole] = useState<PresentationRole>(initialRole);
  const [isExportingPdf, setIsExportingPdf] = useState<boolean>(false);
  const [exportNotice, setExportNotice] = useState<string | null>(null);
  const [isHtmlPreviewOpen, setIsHtmlPreviewOpen] = useState<boolean>(false);

  const {
    themeConfig,
    question,
    lifeStageContext,
    natalPromise,
    phalaBhutaWindows,
    bhavaAssessments,
    convergenceMatrix,
    proReasoningSteps,
    remedies
  } = analysisResult;

  const primaryWindow = phalaBhutaWindows.primary;
  const promiseTier = getNatalPromiseTier(natalPromise.score, lang);
  const timingConfluence = getTimingConfluence(primaryWindow.confidenceScore, lang);

  const clientName = kundali.birthDetails?.name || 'Native Client';
  const birthDate = kundali.birthDetails?.date || 'N/A';
  const birthTime = kundali.birthDetails?.time || 'N/A';
  const birthPlace = kundali.birthDetails?.placeName || 'N/A';
  const consultRef = `VJ-${(birthDate || '2026').replace(/[^0-9]/g, '')}-${(clientName || 'NATIVE').replace(/[^a-zA-Z0-9]/g, '').slice(0, 6).toUpperCase()}`;

  const getDocTitle = () => `${clientName.replace(/[^a-zA-Z0-9_\u0900-\u097F]/g, '_')}_Astrology_Report`;

  const handleDownloadPdf = async () => {
    setIsExportingPdf(true);
    try {
      await downloadElementAsPdf('advanced-print-report-container', getDocTitle());
      setExportNotice(isHi ? 'पीडीएफ सफलतापूर्वक डाउनलोड हो गया!' : 'PDF downloaded successfully!');
      setTimeout(() => setExportNotice(null), 3500);
    } catch (e) {
      console.error(e);
      handleOpenHtmlPreview();
    } finally {
      setIsExportingPdf(false);
    }
  };

  const handleOpenHtmlPreview = () => {
    setIsHtmlPreviewOpen(true);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-astro-primary/80 backdrop-blur-xs p-4 sm:p-6 print:p-0 print:bg-white print:static">
      {/* Control Bar (Hidden in Print) */}
      <div className="max-w-4xl mx-auto mb-4 bg-white dark:bg-astro-secondary p-4 rounded-xl border border-astro-border dark:border-astro-strong-border shadow-lg flex flex-wrap items-center justify-between gap-3 print:hidden">
        <button
          onClick={onClose}
          className="flex items-center gap-2 px-3.5 py-2 text-xs font-semibold text-astro-secondary-text dark:text-astro-muted bg-astro-secondary-surface dark:bg-astro-secondary rounded-xl hover:bg-astro-card transition-all min-h-[40px] touch-manipulation cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{isHi ? 'वापस जाएं' : 'Back to Workspace'}</span>
        </button>

        <div className="flex flex-wrap items-center gap-2">
          {/* Report Role Switcher */}
          <div className="flex items-center bg-astro-secondary-surface dark:bg-astro-secondary p-1 rounded-xl text-xs">
            <button
              onClick={() => setReportRole('BASIC')}
              className={`px-3 py-1.5 rounded-lg font-medium transition-all cursor-pointer ${
                reportRole === 'BASIC'
                  ? 'bg-white dark:bg-astro-secondary text-amber-600 dark:text-amber-400 shadow-xs font-bold'
                  : 'text-astro-secondary-text dark:text-astro-muted'
              }`}
            >
              {isHi ? 'कार्यकारी सारांश' : 'Executive Summary'}
            </button>
            <button
              onClick={() => setReportRole('PRO')}
              className={`px-3 py-1.5 rounded-lg font-medium transition-all cursor-pointer ${
                reportRole === 'PRO'
                  ? 'bg-white dark:bg-astro-secondary text-amber-600 dark:text-amber-400 shadow-xs font-bold'
                  : 'text-astro-secondary-text dark:text-astro-muted'
              }`}
            >
              {isHi ? 'क्लासिकल डोजियर' : 'Classical Dossier'}
            </button>
            <button
              onClick={() => setReportRole('ACHARYA')}
              className={`px-3 py-1.5 rounded-lg font-medium transition-all cursor-pointer ${
                reportRole === 'ACHARYA'
                  ? 'bg-white dark:bg-astro-secondary text-amber-600 dark:text-amber-400 shadow-xs font-bold'
                  : 'text-astro-secondary-text dark:text-astro-muted'
              }`}
            >
              {isHi ? 'आचार्य परामर्श' : 'Acharya Report'}
            </button>
          </div>

          {/* Download PDF */}
          <button
            onClick={handleDownloadPdf}
            disabled={isExportingPdf}
            className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-white bg-astro-primary hover:bg-astro-secondary rounded-xl shadow-md transition-all min-h-[40px] touch-manipulation cursor-pointer disabled:opacity-60"
          >
            {isExportingPdf ? (
              <Loader2 className="w-4 h-4 animate-spin text-astro-accent" />
            ) : (
              <Download className="w-4 h-4 text-astro-accent" />
            )}
            <span>{isHi ? 'डाउनलोड पीडीएफ' : 'Download PDF'}</span>
          </button>

          {/* HTML Print Tool */}
          <button
            onClick={handleOpenHtmlPreview}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-astro-text bg-astro-secondary-surface hover:bg-astro-card border border-astro-border rounded-xl transition-all min-h-[40px] touch-manipulation cursor-pointer"
            title="Open in-app HTML print preview tool"
          >
            <ExternalLink className="w-3.5 h-3.5 text-astro-primary" />
            <span className="hidden sm:inline">{isHi ? 'एचटीएमएल प्रिंट' : 'HTML Print Tool'}</span>
          </button>

          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-3 py-2 text-xs font-bold text-astro-secondary-text bg-astro-secondary-surface hover:bg-astro-card border border-astro-border rounded-xl transition-all min-h-[40px] touch-manipulation cursor-pointer"
          >
            <Printer className="w-3.5 h-3.5 text-astro-muted" />
            <span>{isHi ? 'प्रिंट' : 'Print'}</span>
          </button>
        </div>
      </div>

      {/* Export Notice Toast */}
      {exportNotice && (
        <div className="max-w-4xl mx-auto mb-3 bg-astro-primary text-white text-xs font-bold text-center py-2 rounded-lg shadow-sm animate-fadeIn print:hidden">
          ✓ {exportNotice}
        </div>
      )}

      {/* Printable Report Document Sheet */}
      <div id="advanced-print-report-container" className="max-w-4xl mx-auto bg-white text-astro-text p-6 sm:p-10 rounded-2xl shadow-2xl border border-astro-border print:shadow-none print:border-none print:p-0 space-y-6">
        {/* Document Header */}
        <div className="flex items-start justify-between border-b border-astro-border pb-6">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <SwastikLogo className="w-8 h-8 text-amber-600" />
              <h1 className="text-2xl font-bold font-serif tracking-tight text-astro-text">
                VEDANG JYOTISHAM
              </h1>
            </div>
            <p className="text-xs uppercase tracking-widest text-astro-secondary font-semibold">
              {reportRole === 'BASIC' && (isHi ? 'कार्यकारी फलादेश सारांश' : 'Executive Astrological Summary')}
              {reportRole === 'PRO' && (isHi ? 'प्रामाणिक पराशरीय शास्त्रीय डोजियर' : 'Classical Parashari Forensic Dossier')}
              {reportRole === 'ACHARYA' && (isHi ? 'ज्योतिषाचार्य अधिकृत परामर्श प्रतिवेदन' : 'Official Jyotish Acharya Consultation Report')}
            </p>
            <div className="text-xs text-astro-secondary-text pt-1">
              <strong>{clientName}</strong> •{' '}
              <span>DOB: {birthDate} ({birthTime})</span> •{' '}
              <span>POB: {birthPlace}</span>
              {reportRole === 'ACHARYA' && (
                <span className="font-mono ml-1 font-bold text-astro-secondary">[{consultRef}]</span>
              )}
            </div>
          </div>

          {/* Age & Life Stage Badge */}
          <div className="text-right space-y-1">
            <div className="inline-block px-3 py-1 rounded-lg bg-astro-secondary-surface border border-[#E2DED5] text-astro-secondary text-xs font-bold">
              {lifeStageContext.years}y {lifeStageContext.months}m ({isHi ? lifeStageContext.stage.labelHi : lifeStageContext.stage.labelEn})
            </div>
            <div className="text-[11px] text-astro-secondary-text">
              {isHi ? 'सक्रिय दशा:' : 'Active Dasha:'} <strong>{lifeStageContext.activeDashaContext.mahadasha}–{lifeStageContext.activeDashaContext.antardasha}</strong>
            </div>
          </div>
        </div>

        {/* Section 1: Life Theme & Targeted Inquiry */}
        <div className="p-4 rounded-xl bg-astro-secondary-surface/60 border border-[#E2DED5] space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase font-bold text-astro-secondary tracking-wider">
              {isHi ? 'जीवन क्षेत्र एवं प्रश्न' : 'Life Theme & Astrological Inquiry'}
            </span>
            <span className="text-xs font-bold text-astro-secondary-text">
              {isHi ? themeConfig.nameHi : themeConfig.nameEn}
            </span>
          </div>
          <h2 className="text-base font-bold text-astro-text">
            {isHi ? question.questionHi : question.questionEn}
          </h2>
          <p className="text-xs text-astro-secondary-text">
            {isHi ? question.descriptionHi : question.descriptionEn}
          </p>
        </div>

        {/* Section 2: Natal Promise & Phala-Bhuta Timing Window */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 rounded-xl border border-astro-border space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase text-astro-muted">
                {isHi ? 'जन्मजात फल संभावना' : 'Natal Promise Status'}
              </span>
              <span className={`text-xs font-extrabold px-2.5 py-0.5 rounded-full uppercase ${promiseTier.badgeClass}`}>
                {isHi ? promiseTier.tierHi : promiseTier.tierEn}
              </span>
            </div>
            <h3 className="text-sm font-bold text-astro-text">
              {isHi ? natalPromise.verdictHi : natalPromise.verdictEn}
            </h3>
            <p className="text-xs text-astro-secondary-text leading-relaxed">
              {isHi ? natalPromise.analysisSummaryHi : natalPromise.analysisSummaryEn}
            </p>
          </div>

          <div className="p-4 rounded-xl border border-[#E2DED5] bg-astro-secondary-surface space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase text-astro-secondary">
                {isHi ? 'मुख्य फल-प्रदाता काल' : 'Primary Fructification Window'}
              </span>
              <span className="text-xs font-bold px-2 py-0.5 rounded bg-astro-primary text-white font-mono">
                {timingConfluence.stars} {isHi ? timingConfluence.labelHi : timingConfluence.labelEn}
              </span>
            </div>
            <h3 className="text-sm font-bold text-[#263633]">
              {primaryWindow.startDate} — {primaryWindow.endDate}
            </h3>
            <p className="text-xs text-astro-secondary-text leading-relaxed">
              {isHi ? primaryWindow.astrologicalMechanismHi : primaryWindow.astrologicalMechanismEn}
            </p>
          </div>
        </div>

        {/* Section 3: Charts (D1 + Relevant Thematic Varga) */}
        <div className="space-y-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-astro-muted border-b pb-1">
            {isHi ? 'आधारभूत चक्र' : 'Astrological Charts'}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-2 border rounded-lg flex flex-col items-center">
              <span className="text-xs font-bold mb-1">D1 Rashi Chart</span>
              <div className="w-full max-w-[200px] aspect-square">
                {chartStyle === 'south' ? (
                  <SouthIndianSvgChart chartData={kundali.charts.D1} planets={kundali.planets} lang={lang} />
                ) : (
                  <NorthIndianSvgChart chartData={kundali.charts.D1} planets={kundali.planets} lang={lang} />
                )}
              </div>
            </div>

            <div className="p-2 border rounded-lg flex flex-col items-center">
              <span className="text-xs font-bold mb-1">{analysisResult.relevantVargas[1] || 'D9'} Varga</span>
              <div className="w-full max-w-[200px] aspect-square">
                {(() => {
                  const vCode = analysisResult.relevantVargas[1] || 'D9';
                  const vChart = kundali.charts[vCode] || kundali.charts.D9 || kundali.charts.D1;
                  return chartStyle === 'south' ? (
                    <SouthIndianSvgChart chartData={vChart} planets={vChart?.planets || kundali.planets} lang={lang} />
                  ) : (
                    <NorthIndianSvgChart chartData={vChart} planets={vChart?.planets || kundali.planets} lang={lang} />
                  );
                })()}
              </div>
            </div>
          </div>
        </div>

        {/* Section 4: Pro 5-Step Classical Reasoning */}
        {reportRole === 'PRO' && proReasoningSteps && proReasoningSteps.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-astro-primary border-b pb-1">
              {isHi ? '५-चरणीय शास्त्रीय पराशरीय तर्क प्रणाली' : '5-Step Classical Parashari Reasoning'}
            </h3>
            <div className="space-y-2 text-xs">
              {proReasoningSteps.map((step) => (
                <div key={step.step} className="p-3 bg-astro-secondary-surface border border-astro-border rounded-lg space-y-1">
                  <div className="font-bold text-astro-text">
                    Step {step.step}: {isHi ? step.titleHi : step.titleEn}
                  </div>
                  <p className="text-astro-secondary-text"><strong>Rule:</strong> {step.classicalRule}</p>
                  <p className="text-astro-secondary-text"><strong>Synthesis:</strong> {step.synthesis}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Section 5: Remedies & Guidance */}
        {remedies && remedies.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-astro-muted border-b pb-1">
              {isHi ? 'शास्त्रीय उपाय एवं मार्गदर्शन' : 'Remedial Guidance & Solutions'}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              {remedies.map((rem, i) => (
                <div key={i} className="p-3 bg-amber-50/50 border border-amber-200/60 rounded-xl space-y-1">
                  <span className="font-bold text-amber-900 block">
                    {rem.type}: {isHi ? rem.titleHi : rem.titleEn}
                  </span>
                  <p className="text-astro-secondary-text text-[11px]">
                    {isHi ? rem.instructionHi : rem.instructionEn}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Document Footer Signoff */}
        <div className="pt-6 border-t border-astro-border flex items-center justify-between text-xs text-astro-muted">
          <div>
            <p className="font-bold text-astro-secondary-text">Vedang Jyotisham V6.0 Engine</p>
            <p className="text-[10px]">Deterministic Calculations + AI Synthesis Gateway</p>
          </div>
          <div className="text-right">
            <p className="font-serif font-bold text-astro-primary">Acharya / Astrologer Signoff</p>
            <p className="text-[10px] text-astro-muted">Certified Astrological Workspace</p>
          </div>
        </div>
      </div>
      <HtmlPrintPreviewModal
        isOpen={isHtmlPreviewOpen}
        onClose={() => setIsHtmlPreviewOpen(false)}
        elementId="advanced-print-report-container"
        documentTitle={getDocTitle()}
        paperSize="a4"
      />
    </div>
  );
};
